import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { appNav } from "../routes/appNavigation";
import "./CommandPalette.css";

export type CommandAction = {
  id: string;
  label: string;
  group: string;
  keywords?: string[];
  run: () => void;
};

type Props = {
  open: boolean;
  onClose: () => void;
  actions: CommandAction[];
};

export const CommandPalette: React.FC<Props> = ({ open, onClose, actions }) => {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return actions;
    return actions.filter((action) => {
      const hay = `${action.label} ${action.group} ${(action.keywords ?? []).join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [actions, query]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActiveIndex(0);
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((index) => Math.min(index + 1, Math.max(filtered.length - 1, 0)));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
      } else if (e.key === "Enter" && filtered[activeIndex]) {
        e.preventDefault();
        filtered[activeIndex].run();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, filtered, activeIndex, onClose]);

  if (!open) return null;

  return (
    <div className="command-palette" role="presentation" onMouseDown={onClose}>
      <div
        className="command-palette__dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          className="command-palette__input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Jump to a page or action…"
          aria-label="Search commands"
        />
        <ul className="command-palette__list" role="listbox">
          {filtered.length === 0 ? (
            <li className="command-palette__empty">No matching commands</li>
          ) : (
            filtered.map((action, index) => (
              <li key={action.id}>
                <button
                  type="button"
                  className={`command-palette__item${index === activeIndex ? " is-active" : ""}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => {
                    action.run();
                    onClose();
                  }}
                >
                  <span className="command-palette__label">{action.label}</span>
                  <span className="command-palette__group">{action.group}</span>
                </button>
              </li>
            ))
          )}
        </ul>
        <div className="command-palette__hint">
          <span>↑ ↓ navigate</span>
          <span>↵ run</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
};

export function useCommandPaletteActions(onOpenImport?: () => void): CommandAction[] {
  const navigate = useNavigate();
  return useMemo(
    () => [
      { id: "calendar", label: "Open calendar", group: "Navigate", keywords: ["schedule"], run: () => navigate(appNav.calendar()) },
      { id: "events", label: "Open events list", group: "Navigate", keywords: ["roster"], run: () => navigate(appNav.events()) },
      { id: "new-event", label: "Create new event", group: "Actions", keywords: ["add"], run: () => navigate(appNav.newEvent()) },
      { id: "finance", label: "Open finance hub", group: "Navigate", run: () => navigate(appNav.finance()) },
      { id: "analytics", label: "Open finance analytics", group: "Navigate", run: () => navigate(appNav.financeAnalytics()) },
      { id: "invoices", label: "Open invoice register", group: "Navigate", run: () => navigate(appNav.financeInvoices()) },
      { id: "home", label: "Portfolio snapshot", group: "Navigate", run: () => navigate(appNav.home()) },
      { id: "artists", label: "Open artists directory", group: "Directory", run: () => navigate(appNav.artists()) },
      { id: "venues", label: "Open venues directory", group: "Directory", run: () => navigate(appNav.venues()) },
      { id: "contacts", label: "Open contacts directory", group: "Directory", run: () => navigate(appNav.contacts()) },
      { id: "profile", label: "Open profile", group: "Navigate", run: () => navigate(appNav.profile()) },
      { id: "guests", label: "Open guest list", group: "Directory", keywords: ["rsvp", "guests", "guest list"], run: () => navigate(appNav.guests()) },
      ...(onOpenImport
        ? [{ id: "import-csv", label: "Import events from CSV", group: "Actions", keywords: ["upload", "roster"], run: onOpenImport }]
        : []),
    ],
    [navigate, onOpenImport],
  );
}

export function useCommandPaletteHotkey(onOpen: () => void): void {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isK = e.key?.toLowerCase() === "k";
      if (!isK || !(e.metaKey || e.ctrlKey)) return;
      const target = e.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      onOpen();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onOpen]);
}
